import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";
import type { Page } from "./page";

/** Uživatelé — `/admin/api/v1/users` (API chain, JWT cookie, CSRF off). */
export interface UserSummary {
  id: string;
  username: string;
  displayName: string | null;
  email: string | null;
  authSource: string;
  externalIdentifier: string | null;
  enabled: boolean;
  mustChangePassword: boolean;
  bootstrap: boolean;
  roles: string[];
  lastLoginAt: string | null;
  lockedUntil: string | null;
  createdAt: string | null;
}

export const USER_ROLES = ["ROLE_ADMIN", "ROLE_OPERATOR", "ROLE_VIEWER"] as const;

export interface UserCreateBody {
  username: string;
  displayName?: string;
  email?: string;
  password?: string;
  roles: string[];
  /** LOCAL nebo LDAP:<providerId>. Default LOCAL. */
  authSource?: string;
  /** Externí identifikátor (DN/UPN) pro LDAP uživatele. */
  externalIdentifier?: string;
}

export interface AuthSourceChangeBody {
  authSource: string;
  externalIdentifier?: string;
  newPassword?: string;
}
export interface UserUpdateBody {
  displayName?: string;
  email?: string;
  roles: string[];
  enabled: boolean;
}

export function useUsers() {
  return useQuery({ queryKey: ["users"], queryFn: () => apiFetch<UserSummary[]>("/admin/api/v1/users") });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateBody) => apiFetch<UserSummary>("/admin/api/v1/users", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserUpdateBody) => apiFetch<UserSummary>(`/admin/api/v1/users/${id}`, { method: "PATCH", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** Změna auth-source uživatele (LOCAL ↔ LDAP:<provider>). */
export function useChangeAuthSource(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AuthSourceChangeBody) =>
      apiFetch<UserSummary>(`/admin/api/v1/users/${id}/auth-source`, { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/api/v1/users/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useResetPassword(id: string) {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<void>(`/admin/api/v1/users/${id}/reset-password`, { method: "POST", json: { password } }),
  });
}

/** Krátký label role bez ROLE_ prefixu. */
export function roleLabel(role: string): string {
  return role.replace(/^ROLE_/, "");
}

/* ----------------------------- Auth audit log ----------------------------- */

export interface AuditEntry {
  id: number;
  eventType: string;
  outcome: string;
  username: string | null;
  actor: string | null;
  ipAddress: string | null;
  target: string | null;
  details: Record<string, unknown> | null;
  timestamp: string;
}

export interface AuditFilter {
  username?: string;
  eventType?: string;
  outcome?: string;
  actor?: string;
  ipAddress?: string;
  target?: string;
  from?: string;
  to?: string;
  search?: string;
}

function auditQs(page: number, size: number, filter: AuditFilter): URLSearchParams {
  const p = new URLSearchParams({ page: String(page), size: String(size) });
  for (const [k, v] of Object.entries(filter)) if (v && v.trim()) p.set(k, v.trim());
  return p;
}

export function useAuditLog(page: number, size: number, filter: AuditFilter = {}, refetchIntervalMs = 0) {
  const p = auditQs(page, size, filter);
  return useQuery({
    queryKey: ["audit", page, size, p.toString()],
    queryFn: () => apiFetch<Page<AuditEntry>>(`/admin/api/v1/audit?${p.toString()}`),
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
  });
}

/**
 * URL server-side exportu s aktuálními filtry. Na rozdíl od listu není stránkovaný
 * a nese i `userAgent` a `details`. Vzor: `commLogExportUrl` v `lib/logs.ts`.
 */
export function auditExportUrl(format: "json" | "csv", filter: AuditFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) if (v && v.trim()) p.set(k, v.trim());
  p.set("format", format);
  return `/admin/api/v1/audit/export?${p.toString()}`;
}
